/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, put, post, postFiles, patch } from "../../config/axios";
import {
  Button,
  Box,
  Modal,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Searchbar from "../../components/Custom/SearchBar/Searchbar";
import DeleteModal from "../../components/Custom/DeleteModal/DeleteModal";
import { deleteAPI } from "../../helper/apiCallHelper";
import { useDebouncedValue } from "../../helper/debounce";
import { toastMessage } from "../../utils/toastMessage";
import FormModal from "../../components/Custom/FormModal/FormModal";
import {
  cbtContentTableColumns,
  cbtContentFormFields,
} from "../../constants/cbtContentPage";
import styles from "./CbtContent.module.css";
import moment from "moment";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';

const CbtContent = () => {
  const [content, setContent] = useState([]);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteContent, setDeleteContent] = useState("");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const debouncedSearch = useDebouncedValue(search, 500);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState({});
  const [previewModal, setPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Fetch CBT Content
  const fetchContent = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: perPage,
        search: debouncedSearch,
        status: statusFilter,
      };

      const response = await get("/api/dashboard/cbt/content", params);
      
      if (response && response.success) {
        setContent(response.data.content);
        setTotalData(response.data.pagination.totalContent);
        setPageCount(response.data.pagination.totalPages);
      } else {
        toastMessage("error", response?.message || "Failed to fetch CBT content");
      }
    } catch (error) {
      console.error("Error fetching CBT content:", error);
      toastMessage("error", "Failed to fetch CBT content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [currentPage, perPage, debouncedSearch, statusFilter]);

  // Handle search
  const handleSearch = (searchValue) => {
    setSearch(searchValue);
    setCurrentPage(1);
  };

  // Handle page change
  const handleChange = (page) => {
    setCurrentPage(page);
  };

  // Open modal for adding new content
  const openModal = (type) => {
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    }
  };

  // Handle edit
  const handleEdit = (row) => {
    setEditData(row);
    setEditModal(true);
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = (row) => {
    setDeleteContent(row);
    setDeleteModalOpen(true);
  };

  // Handle view
  const handleView = (row) => {
    setViewData(row);
    setViewModal(true);
  };

  // Handle status toggle
  const handleStatus = async (row, newStatus) => {
    try {
      const response = await put(`/api/dashboard/cbt/content/${row._id}`, {
        status: newStatus,
      });

      if (response.success) {
        toastMessage("success", "Status updated successfully");
        fetchContent();
      } else {
        toastMessage("error", response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toastMessage("error", "Failed to update status");
    }
  };

  // Toggle status active/inactive from table
  const handleActive = async (id, active) => {
    try {
      const response = await put(`/api/dashboard/cbt/content/${id}`, {
        status: active ? "active" : "inactive",
      });
      if (response?.success) {
        toastMessage("success", active ? "Activated" : "Deactivated");
        fetchContent();
      } else {
        toastMessage("error", response?.message || "Failed to update status");
      }
    } catch (e) {
      toastMessage("error", "Failed to update status");
    }
  };

  // Handle form submission
  const handleFormSubmit = async (formData, isEditingArg, id) => {
    try {
      let response;

      // Upload files if File objects are present
      const payload = { ...formData };
      
      // Debug: Log the form data and readTime
      console.log("Form data received:", formData);
      console.log("Read time value:", formData.readTime);
      console.log("Read time type:", typeof formData.readTime);

      if (payload.attachedFile && payload.attachedFile instanceof File) {
        const fileData = new FormData();
        fileData.append("file", payload.attachedFile);
        const uploadRes = await postFiles("/api/dashboard/cbt/content/upload-file", fileData);
        if (!uploadRes?.data?.success) throw new Error(uploadRes?.data?.message || "File upload failed");
        payload.attachedFile = uploadRes.data.fileUrl;
      }

      if (payload.thumbnail && payload.thumbnail instanceof File) {
        const thumbData = new FormData();
        thumbData.append("thumbnail", payload.thumbnail);
        const uploadThumbRes = await postFiles("/api/dashboard/cbt/content/upload-thumbnail", thumbData);
        if (!uploadThumbRes?.data?.success) throw new Error(uploadThumbRes?.data?.message || "Thumbnail upload failed");
        payload.thumbnail = uploadThumbRes.data.thumbnailUrl;
      }
      
      // Ensure readTime is properly converted to number
      if (payload.readTime !== undefined && payload.readTime !== null && payload.readTime !== '') {
        payload.readTime = Number(payload.readTime);
      } else {
        payload.readTime = 0;
      }
      
      // Debug: Log the payload being sent
      console.log("Payload being sent to backend:", payload);
      console.log("Read time in payload:", payload.readTime);
      
      if (isEditingArg) {
        // Update existing content
        response = await put(`/api/dashboard/cbt/content/${id}`, payload);
      } else {
        // Create new content
        response = await post("/api/dashboard/cbt/content", payload);
      }
      
      // Debug: Log the response
      console.log("Backend response:", response);

      const ok = response?.success || response?.data?.success;
      if (ok) {
        toastMessage("success", isEditingArg ? "Content updated successfully" : "Content created successfully");
        setIsModalOpen(false);
        setEditModal(false);
        setEditData({});
        fetchContent();
      } else {
        toastMessage("error", response?.message || response?.data?.message || "Failed to save content");
      }
    } catch (error) {
      console.error("Error saving content:", error);
      toastMessage("error", "Failed to save content");
    }
  };

  // Handle file upload
  const handleFileUpload = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append(type === "thumbnail" ? "thumbnail" : "file", file);

      const endpoint = type === "thumbnail" 
        ? "/dashboard/cbt/content/upload-thumbnail"
        : "/dashboard/cbt/content/upload-file";

      const response = await postFiles(`/api${endpoint}`, formData);
      
      if (response?.data?.success) {
        const fileUrl = type === "thumbnail" ? response.data.thumbnailUrl : response.data.fileUrl;
        toastMessage("success", `${type === "thumbnail" ? "Thumbnail" : "File"} uploaded successfully`);
        return fileUrl;
      } else {
        toastMessage("error", response?.message || response?.data?.message || `Failed to upload ${type}`);
        return null;
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      toastMessage("error", `Failed to upload ${type}`);
      return null;
    }
  };

  // Handle delete confirmation
  const handleDeleteUser = async () => {
    try {
      const response = await deleteAPI(`/api/dashboard/cbt/content/${deleteContent._id}`);
      
      if (response.success) {
        toastMessage("success", "Content deleted successfully");
        setDeleteModalOpen(false);
        setDeleteContent("");
        fetchContent();
      } else {
        toastMessage("error", response.message || "Failed to delete content");
      }
    } catch (error) {
      console.error("Error deleting content:", error);
      toastMessage("error", "Failed to delete content");
    }
  };

  // Handle close delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteContent("");
  };

  // Handle download
  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'cbt-content-file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      toastMessage("error", "Failed to download file");
    }
  };

  // Handle preview
  const handlePreview = (fileUrl) => {
    setPreviewUrl(fileUrl);
    setPreviewModal(true);
  };

  // Close preview modal
  const handleClosePreview = () => {
    setPreviewModal(false);
    setPreviewUrl("");
  };

  // Close form modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditModal(false);
    setEditData({});
  };

  // Close view modal
  const handleCloseViewModal = () => {
    setViewModal(false);
    setViewData({});
  };

  return (
    <>
      <Layout>
        <div style={{ padding: "1rem" }}>
          <Typography variant="h5">CBT Content Management</Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ width: "40%" }}>
              <Searchbar
                search={handleSearch}
                placeholder={"Search by title or description"}
                debounceTime={1000}
              />
            </div>

            <Button
              onClick={() => openModal("add")}
              variant="outlined"
              startIcon={<AddIcon fontSize="large" />}
              style={{ fontWeight: "bold" }}
            >
              Add New CBT Content
            </Button>
          </div>

          <CustomTable
            data={content}
            columns={cbtContentTableColumns}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleStatus={handleStatus}
            handleDisplay={handleView}
            handleActive={handleActive}
            handlePageChange={(page) => handleChange(page)}
            pageNumber={page}
            pageCount={pageCount}
            totalData={totalData}
            perPage={perPage}
            currentPage={currentPage}
            loading={loading}
          />
          {search && content.length === 0 && !loading && (
            <Typography variant="body1" sx={{ color: 'red', textAlign: 'center', mt: 2 }}>
              No CBT content found for your search.
            </Typography>
          )}
        </div>
      </Layout>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteUser}
        data={deleteContent}
      />

      {/* Form Modal */}
      <FormModal
        accept="*/*"
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleFormSubmit}
        fields={editModal ? cbtContentFormFields : cbtContentFormFields.filter(f => f.name !== 'status')}
        initialData={editData}
        isEditing={editModal}
        header={editModal ? "Edit CBT Content" : "Add New CBT Content"}
      />

      {/* View Modal */}
      <Dialog open={viewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            CBT Content Details
          </Typography>
          {viewData && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Title:</strong> {viewData.title}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Description:</strong> {viewData.description}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> {viewData.status}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Created By:</strong> {viewData.createdBy?.fullname}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Created Date:</strong> {moment(viewData.createdAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Updated Date:</strong> {moment(viewData.updatedAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
              
              {viewData.attachedFile && (
                <Box mt={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Attached File:</strong>
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(viewData.attachedFile, viewData.title)}
                  >
                    Download File
                  </Button>
                </Box>
              )}

              {viewData.thumbnail && (
                <Box mt={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Thumbnail:</strong>
                  </Typography>
                  <img 
                    src={viewData.thumbnail} 
                    alt="Thumbnail" 
                    style={{ maxWidth: "200px", maxHeight: "200px" }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewModal} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            {previewUrl && (
              <Box>
                {previewUrl.includes('.pdf') ? (
                  <iframe
                    src={previewUrl}
                    width="100%"
                    height="500px"
                    style={{ border: 'none' }}
                  />
                ) : previewUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "500px" }}
                  />
                ) : previewUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                  <video
                    src={previewUrl}
                    controls
                    style={{ maxWidth: "100%", maxHeight: "500px" }}
                  />
                ) : (
                  <Typography variant="body1">
                    Preview not available for this file type
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CbtContent;
